import { motion } from "framer-motion";
import { Heart, BookOpen, Palette, Printer } from "lucide-react";
import { Seo } from "@/components/Seo";

const VALUES = [
  { Icon: BookOpen, color: "#3B82F6", title: "Made for Little Learners", text: "Every page is designed for children aged 3-6, with big letters, friendly layouts and joyful themes." },
  { Icon: Palette, color: "#EF4444", title: "Colorful & Fun", text: "We believe learning should feel like play — bright colours, cute illustrations and activities kids ask for." },
  { Icon: Printer, color: "#22C55E", title: "Print & Reuse", text: "All our resources are printable PDFs. Print at home or school, as many times as you like." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="mx-auto max-w-4xl px-6 py-16">
      <Seo
        title="About Us | Divine Kids Learning"
        description="Divine Kids Learning creates fun, colorful and printable educational resources for young children aged 3-6."
      />
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <p className="font-display text-sm font-bold uppercase tracking-[0.3em] text-brand-purple">About Us</p>
        <h1 className="mt-3 font-display text-4xl font-black tracking-tight text-slate-800 sm:text-5xl">
          Learning made joyful
        </h1>
        <p className="mt-6 text-lg font-semibold leading-relaxed text-slate-600">
          Divine Kids Learning creates fun, colorful and printable educational resources for young
          children. Our workbooks and worksheets help preschoolers build early skills — numbers,
          letters, tracing, counting and colouring — through play-based activities that little
          learners genuinely love.
        </p>
        <p className="mt-4 text-lg font-semibold leading-relaxed text-slate-600">
          Whether you are a parent looking for screen-free learning at home, a homeschooler
          building your curriculum, or a teacher brightening up your classroom, our printable PDFs
          are ready in minutes: download, print, and start learning together.
        </p>
        <p className="mt-6 flex items-center gap-2 font-display text-xl font-bold text-slate-700">
          Small Steps <Heart className="h-5 w-5 fill-brand-red text-brand-red" /> Big Dreams
        </p>
      </motion.div>

      <div className="mt-14 grid gap-6 sm:grid-cols-3">
        {VALUES.map((v, i) => (
          <motion.div
            key={v.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className="rounded-[2rem] bg-white p-7 shadow-xl shadow-blue-900/5"
          >
            <span className="flex h-14 w-14 items-center justify-center rounded-full" style={{ backgroundColor: `${v.color}1A` }}>
              <v.Icon className="h-7 w-7" style={{ color: v.color }} />
            </span>
            <h2 className="mt-4 font-display text-lg font-bold text-slate-800">{v.title}</h2>
            <p className="mt-2 text-sm font-semibold leading-relaxed text-slate-500">{v.text}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
